import {
  isUserDataIdentifier,
  normalizeUserDataPath,
} from "./userState";

export type ConditionalRuleAction =
  | "show"
  | "hide"
  | "enable"
  | "disable"
  | "start"
  | "stop";
export type ConditionalControlAction = "start" | "stop";
export type ConditionSourceType =
  | "answer"
  | "user-state"
  | "user-metric"
  | "component-state";
export type ConditionValueType =
  | "string"
  | "file"
  | "number"
  | "boolean"
  | "enum"
  | "duration"
  | "percentage";
export type ConditionOperator =
  | "Equals"
  | "Not Equals"
  | "Greater than"
  | "Greater than or equal"
  | "Less than"
  | "Less than or equal"
  | "Contains"
  | "Does not contain";

export interface AnswerConditionSource {
  type: "answer";
  blockId: string;
}

export interface UserStateConditionSource {
  type: "user-state";
  dataPath: string[];
  key: string;
  valueType: "string" | "file" | "number" | "boolean";
}

export interface UserMetricConditionSource {
  type: "user-metric";
  dataPath: string[];
  key: string;
}

export interface ComponentStateConditionSource {
  type: "component-state";
  blockId: string;
  field: string;
}

export type ConditionSource =
  | AnswerConditionSource
  | UserStateConditionSource
  | UserMetricConditionSource
  | ComponentStateConditionSource;

export interface ConditionalRule {
  id?: string;
  source?: ConditionSource;
  /** Legacy answer-source block ID. */
  block?: string;
  condition?: string;
  value?: unknown;
  action?: string;
}

export interface ConditionalRuleState {
  visible: boolean;
  enabled: boolean;
}

export interface ConditionalRuleEvaluation extends ConditionalRuleState {
  hasVisibilityRules: boolean;
  hasEnabledRules: boolean;
  hasControlRules: boolean;
  visibilityReady: boolean;
  enabledReady: boolean;
  controlReady: boolean;
  controlAction: ConditionalControlAction | null;
}

export interface ConditionSourceDefinition {
  type: ConditionSourceType;
  label: string;
  description: string;
  icon: string;
}

export interface ConditionStateField {
  key: string;
  label: string;
  description?: string;
  valueType: ConditionValueType;
  options?: Array<{ label: string; value: string | number | boolean }>;
  unit?: string;
}

export interface ConditionalRequirements {
  answers: string[];
  userStates: UserStateConditionSource[];
  userMetrics: UserMetricConditionSource[];
  componentStates: ComponentStateConditionSource[];
}

export interface ConditionalRuleValidationOptions {
  currentBlockId?: string;
  blockIds?: ReadonlySet<string>;
  getComponentStateFields?: (blockId: string) => readonly string[];
  allowedActions?: readonly ConditionalRuleAction[];
  requireAction?: boolean;
}

export interface ConditionalRuleValidationError {
  ruleId?: string;
  index: number;
  field: "source" | "condition" | "value" | "action";
  message: string;
}

export const GENERIC_COMPONENT_STATE_FIELDS: readonly ConditionStateField[] = [
  {
    key: "visible",
    label: "Visible",
    description: "Whether conditional rules allow this block to render.",
    valueType: "boolean",
  },
  {
    key: "enabled",
    label: "Enabled",
    description: "Whether conditional rules allow interaction with this block.",
    valueType: "boolean",
  },
] as const;

export const CONDITION_SOURCE_DEFINITIONS: readonly ConditionSourceDefinition[] = [
  {
    type: "answer",
    label: "Assessment answer",
    description: "A value answered in an assessment block.",
    icon: "lucide:list-checks",
  },
  {
    type: "user-state",
    label: "User state",
    description: "A typed per-user value written by an automation.",
    icon: "lucide:user-round-cog",
  },
  {
    type: "user-metric",
    label: "User metric",
    description: "A numeric per-user metric written by an automation.",
    icon: "lucide:gauge",
  },
  {
    type: "component-state",
    label: "Component state",
    description: "A live state field exposed by another component.",
    icon: "lucide:component",
  },
] as const;

export const CONDITION_OPERATOR_DEFINITIONS: ReadonlyArray<{
  value: ConditionOperator;
  label: string;
}> = [
  { value: "Equals", label: "Equals" },
  { value: "Not Equals", label: "Does not equal" },
  { value: "Greater than", label: "Is greater than" },
  { value: "Greater than or equal", label: "Is at least" },
  { value: "Less than", label: "Is less than" },
  { value: "Less than or equal", label: "Is at most" },
  { value: "Contains", label: "Contains" },
  { value: "Does not contain", label: "Does not contain" },
] as const;

export const CONDITIONAL_RULE_ACTION_DEFINITIONS: ReadonlyArray<{
  value: ConditionalRuleAction;
  label: string;
}> = [
  { value: "show", label: "Show" },
  { value: "hide", label: "Hide" },
  { value: "enable", label: "Enable" },
  { value: "disable", label: "Disable" },
  { value: "start", label: "Start" },
  { value: "stop", label: "Stop" },
] as const;

export const DEFAULT_CONDITIONAL_RULE_ACTIONS: readonly ConditionalRuleAction[] = [
  "show",
  "hide",
  "enable",
  "disable",
] as const;

const STRING_OPERATORS: readonly ConditionOperator[] = [
  "Equals",
  "Not Equals",
  "Contains",
  "Does not contain",
];
const NUMBER_OPERATORS: readonly ConditionOperator[] = [
  "Equals",
  "Not Equals",
  "Greater than",
  "Greater than or equal",
  "Less than",
  "Less than or equal",
];
const EQUALITY_OPERATORS: readonly ConditionOperator[] = [
  "Equals",
  "Not Equals",
];
export function getAllowedConditionOperators(
  valueType: ConditionValueType,
): readonly ConditionOperator[] {
  if (
    valueType === "number"
    || valueType === "duration"
    || valueType === "percentage"
  ) {
    return NUMBER_OPERATORS;
  }
  if (valueType === "boolean" || valueType === "enum") {
    return EQUALITY_OPERATORS;
  }
  return STRING_OPERATORS;
}

export function normalizeConditionalRuleAction(
  value: unknown,
): ConditionalRuleAction | null {
  const action = typeof value === "string" ? value.trim().toLowerCase() : "";
  return action === "show"
    || action === "hide"
    || action === "enable"
    || action === "disable"
    || action === "start"
    || action === "stop"
    ? action
    : null;
}

function normalizeOperator(value: unknown): string {
  return typeof value === "string"
    ? value.trim().toLowerCase().replace(/\s+/g, " ")
    : "";
}

function nonEmptyString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeConditionSource(rule: ConditionalRule): ConditionSource | null {
  const source = rule?.source;
  if (source?.type === "answer") {
    const blockId = nonEmptyString(source.blockId);
    return blockId ? { type: "answer", blockId } : null;
  }
  if (source?.type === "user-state") {
    const key = nonEmptyString(source.key);
    const valueType =
      source.valueType === "number"
      || source.valueType === "boolean"
      || source.valueType === "file"
        ? source.valueType
        : "string";
    try {
      return isUserDataIdentifier(key)
        ? {
            type: "user-state",
            dataPath: normalizeUserDataPath(source.dataPath),
            key,
            valueType,
          }
        : null;
    } catch {
      return null;
    }
  }
  if (source?.type === "user-metric") {
    const key = nonEmptyString(source.key);
    try {
      return isUserDataIdentifier(key)
        ? {
            type: "user-metric",
            dataPath: normalizeUserDataPath(source.dataPath),
            key,
          }
        : null;
    } catch {
      return null;
    }
  }
  if (source?.type === "component-state") {
    const blockId = nonEmptyString(source.blockId);
    const field = nonEmptyString(source.field);
    return blockId && field
      ? { type: "component-state", blockId, field }
      : null;
  }

  const legacyBlockId = nonEmptyString(rule?.block);
  return legacyBlockId ? { type: "answer", blockId: legacyBlockId } : null;
}

export function normalizeConditionalRule(rule: unknown): ConditionalRule | null {
  if (!rule || typeof rule !== "object" || Array.isArray(rule)) {
    return null;
  }

  const candidate = rule as ConditionalRule;
  const source = normalizeConditionSource(candidate);
  return {
    id: nonEmptyString(candidate.id) || undefined,
    source: source ?? candidate.source,
    condition: nonEmptyString(candidate.condition),
    value: candidate.value,
    action: nonEmptyString(candidate.action),
  };
}

export function normalizeConditionalRules(rules: unknown): ConditionalRule[] {
  if (!Array.isArray(rules)) return [];
  return rules
    .map(normalizeConditionalRule)
    .filter((rule): rule is ConditionalRule => rule !== null);
}

export function validateConditionalRules(
  rules: unknown,
  options: ConditionalRuleValidationOptions = {},
): ConditionalRuleValidationError[] {
  const errors: ConditionalRuleValidationError[] = [];
  const normalized = normalizeConditionalRules(rules);

  normalized.forEach((rule, index) => {
    const source = normalizeConditionSource(rule);
    const add = (
      field: ConditionalRuleValidationError["field"],
      message: string,
    ) => errors.push({ ruleId: rule.id, index, field, message });

    if (!source) {
      add("source", "Choose and complete a condition source.");
    } else {
      if (
        source.type === "user-state"
        && !isUserDataIdentifier(source.key)
      ) {
        add("source", "State keys may contain only letters, numbers, dots, underscores, and hyphens.");
      }
      if (
        source.type === "user-metric"
        && !isUserDataIdentifier(source.key)
      ) {
        add("source", "Metric keys may contain only letters, numbers, dots, underscores, and hyphens.");
      }
      const blockId =
        source.type === "answer" || source.type === "component-state"
          ? source.blockId
          : null;
      if (blockId && blockId === options.currentBlockId) {
        add("source", "A block cannot depend on its own value or state.");
      } else if (blockId && options.blockIds && !options.blockIds.has(blockId)) {
        add("source", "The referenced block no longer exists.");
      }
      if (
        source.type === "component-state"
        && options.getComponentStateFields
        && !options.getComponentStateFields(source.blockId).includes(source.field)
      ) {
        add("source", "The referenced component state field is unavailable.");
      }
    }

    if (!normalizeOperator(rule.condition)) {
      add("condition", "Choose a comparison.");
    }
    if (options.requireAction !== false) {
      const action = normalizeConditionalRuleAction(rule.action);
      if (!action) {
        add("action", "Choose a result action.");
      } else if (
        options.allowedActions
        && !options.allowedActions.includes(action)
      ) {
        add("action", "This action is unavailable for the selected block.");
      }
    }
    if (rule.value === undefined || rule.value === null || rule.value === "") {
      add("value", "Enter or choose a comparison value.");
    }
  });

  return errors;
}

export function findComponentStateConditionCycles(blocks: unknown): string[][] {
  const graph = new Map<string, Set<string>>();

  function visit(nodes: unknown) {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const block = node as {
        id?: unknown;
        conditions?: unknown;
        children?: unknown;
        items?: unknown;
      };
      const blockId = nonEmptyString(block.id);
      if (blockId) {
        const dependencies = graph.get(blockId) ?? new Set<string>();
        const ruleSets = [block.conditions];
        if (Array.isArray(block.items)) {
          for (const item of block.items) {
            if (!item || typeof item !== "object") continue;
            const variants = (item as { variants?: unknown }).variants;
            if (!Array.isArray(variants)) continue;
            for (const variant of variants) {
              if (!variant || typeof variant !== "object") continue;
              ruleSets.push((variant as { conditions?: unknown }).conditions);
            }
          }
        }
        for (const rules of ruleSets) {
          for (const rule of normalizeConditionalRules(rules)) {
            const source = normalizeConditionSource(rule);
            if (source?.type === "component-state") {
              dependencies.add(source.blockId);
            }
          }
        }
        graph.set(blockId, dependencies);
      }
      visit(block.children);
    }
  }
  visit(blocks);

  const cycles: string[][] = [];
  const visited = new Set<string>();
  const active = new Set<string>();
  const stack: string[] = [];
  const fingerprints = new Set<string>();

  function walk(blockId: string) {
    if (active.has(blockId)) {
      const start = stack.indexOf(blockId);
      const cycle = [...stack.slice(start), blockId];
      const fingerprint = [...new Set(cycle)].sort().join("|");
      if (!fingerprints.has(fingerprint)) {
        fingerprints.add(fingerprint);
        cycles.push(cycle);
      }
      return;
    }
    if (visited.has(blockId)) return;

    active.add(blockId);
    stack.push(blockId);
    for (const dependency of graph.get(blockId) ?? []) {
      if (graph.has(dependency)) walk(dependency);
    }
    stack.pop();
    active.delete(blockId);
    visited.add(blockId);
  }

  for (const blockId of graph.keys()) walk(blockId);
  return cycles;
}

export function conditionSourceKey(source: ConditionSource): string {
  if (source.type === "answer") return `answer:${source.blockId}`;
  if (source.type === "user-state") {
    return `user-state:${[...source.dataPath, source.key].join("/")}`;
  }
  if (source.type === "user-metric") {
    return `user-metric:${[...source.dataPath, source.key].join("/")}`;
  }
  return `component-state:${source.blockId}:${source.field}`;
}

export function collectConditionalRequirements(
  rules: unknown,
): ConditionalRequirements {
  const answers = new Map<string, string>();
  const userStates = new Map<string, UserStateConditionSource>();
  const userMetrics = new Map<string, UserMetricConditionSource>();
  const componentStates = new Map<string, ComponentStateConditionSource>();

  for (const rule of normalizeConditionalRules(rules)) {
    const source = normalizeConditionSource(rule);
    if (!source) continue;
    const key = conditionSourceKey(source);
    if (source.type === "answer") answers.set(key, source.blockId);
    if (source.type === "user-state") userStates.set(key, source);
    if (source.type === "user-metric") userMetrics.set(key, source);
    if (source.type === "component-state") componentStates.set(key, source);
  }

  return {
    answers: [...answers.values()],
    userStates: [...userStates.values()],
    userMetrics: [...userMetrics.values()],
    componentStates: [...componentStates.values()],
  };
}

export function collectResourceConditionalRequirements(
  blocks: unknown,
): ConditionalRequirements {
  const combined: ConditionalRequirements = {
    answers: [],
    userStates: [],
    userMetrics: [],
    componentStates: [],
  };
  const answerKeys = new Set<string>();
  const stateKeys = new Set<string>();
  const metricKeys = new Set<string>();
  const componentKeys = new Set<string>();

  function visit(nodes: unknown) {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const value = node as {
        conditions?: unknown;
        children?: unknown;
        items?: unknown;
      };
      const ruleSets = [value.conditions];
      if (Array.isArray(value.items)) {
        for (const item of value.items) {
          if (!item || typeof item !== "object") continue;
          const variants = (item as { variants?: unknown }).variants;
          if (!Array.isArray(variants)) continue;
          for (const variant of variants) {
            if (!variant || typeof variant !== "object") continue;
            ruleSets.push((variant as { conditions?: unknown }).conditions);
          }
        }
      }

      for (const rules of ruleSets) {
        const requirements = collectConditionalRequirements(rules);
        for (const answer of requirements.answers) {
          if (!answerKeys.has(answer)) {
            answerKeys.add(answer);
            combined.answers.push(answer);
          }
        }
        for (const state of requirements.userStates) {
          const key = conditionSourceKey(state);
          if (!stateKeys.has(key)) {
            stateKeys.add(key);
            combined.userStates.push(state);
          }
        }
        for (const metric of requirements.userMetrics) {
          const key = conditionSourceKey(metric);
          if (!metricKeys.has(key)) {
            metricKeys.add(key);
            combined.userMetrics.push(metric);
          }
        }
        for (const component of requirements.componentStates) {
          const key = conditionSourceKey(component);
          if (!componentKeys.has(key)) {
            componentKeys.add(key);
            combined.componentStates.push(component);
          }
        }
      }
      visit(value.children);
    }
  }

  visit(blocks);
  return combined;
}

function equals(actual: unknown, expected: unknown): boolean {
  if (Array.isArray(actual)) {
    return actual.some((value) => equals(value, expected));
  }
  if (actual === null || actual === undefined) return false;
  if (typeof actual === "boolean") {
    const normalizedExpected =
      expected === true || expected === "true"
        ? true
        : expected === false || expected === "false"
          ? false
          : expected;
    return actual === normalizedExpected;
  }
  return String(actual) === String(expected);
}

function contains(actual: unknown, expected: unknown): boolean {
  if (Array.isArray(actual)) {
    return actual.some((value) => equals(value, expected));
  }
  if (actual === null || actual === undefined) return false;
  return String(actual).includes(String(expected));
}

function compareNumbers(
  actual: unknown,
  expected: unknown,
  compare: (left: number, right: number) => boolean,
): boolean {
  if (Array.isArray(actual)) {
    return actual.some((value) => compareNumbers(value, expected, compare));
  }
  if (
    actual === null
    || actual === undefined
    || expected === null
    || expected === undefined
  ) {
    return false;
  }
  const left = Number(actual);
  const right = Number(expected);
  return Number.isFinite(left) && Number.isFinite(right) && compare(left, right);
}

export function isAnswerConditionMet(
  answerValue: unknown,
  condition: unknown,
  expectedValue: unknown,
): boolean {
  if (answerValue === null || answerValue === undefined) return false;

  switch (normalizeOperator(condition)) {
    case "equals":
      return equals(answerValue, expectedValue);
    case "not equals":
      return !equals(answerValue, expectedValue);
    case "greater than":
      return compareNumbers(answerValue, expectedValue, (left, right) => left > right);
    case "greater than or equal":
      return compareNumbers(answerValue, expectedValue, (left, right) => left >= right);
    case "less than":
      return compareNumbers(answerValue, expectedValue, (left, right) => left < right);
    case "less than or equal":
      return compareNumbers(answerValue, expectedValue, (left, right) => left <= right);
    case "contains":
      return contains(answerValue, expectedValue);
    case "does not contain":
      return !contains(answerValue, expectedValue);
    default:
      return false;
  }
}

export function getMissingUserDataDefault(
  source: ConditionSource,
): string | number | boolean | undefined {
  if (source.type === "user-metric") return 0;
  if (source.type !== "user-state") return undefined;

  if (source.valueType === "number") return 0;
  if (source.valueType === "boolean") return false;
  return "";
}

export function evaluateConditionalRules(
  rules: unknown,
  resolveValue: (source: ConditionSource) => unknown,
  baseline: ConditionalRuleState = { visible: true, enabled: true },
  isAvailable: (source: ConditionSource) => boolean = () => true,
): ConditionalRuleEvaluation {
  const normalizedRules = normalizeConditionalRules(rules);
  if (normalizedRules.length === 0) {
    return {
      ...baseline,
      hasVisibilityRules: false,
      hasEnabledRules: false,
      hasControlRules: false,
      visibilityReady: true,
      enabledReady: true,
      controlReady: true,
      controlAction: null,
    };
  }

  let hasShowRule = false;
  let hasHideRule = false;
  let hasEnableRule = false;
  let hasDisableRule = false;
  let showMatched = false;
  let hideMatched = false;
  let enableMatched = false;
  let disableMatched = false;
  let hasStartRule = false;
  let hasStopRule = false;
  let startMatched = false;
  let stopMatched = false;
  let visibilityReady = true;
  let enabledReady = true;
  let controlReady = true;

  for (const rule of normalizedRules) {
    const action = normalizeConditionalRuleAction(rule.action);
    const source = normalizeConditionSource(rule);
    if (!action || !source) continue;

    if (action === "show") hasShowRule = true;
    if (action === "hide") hasHideRule = true;
    if (action === "enable") hasEnableRule = true;
    if (action === "disable") hasDisableRule = true;
    if (action === "start") hasStartRule = true;
    if (action === "stop") hasStopRule = true;

    let actualValue: unknown;
    if (!isAvailable(source)) {
      const missingDefault = getMissingUserDataDefault(source);
      if (missingDefault === undefined) {
        if (source.type === "component-state") {
          if (action === "show" || action === "hide") visibilityReady = false;
          if (action === "enable" || action === "disable") enabledReady = false;
          if (action === "start" || action === "stop") controlReady = false;
        }
        continue;
      }
      actualValue = missingDefault;
    } else {
      actualValue = resolveValue(source);
    }

    const matches = isAnswerConditionMet(
      actualValue,
      rule.condition,
      rule.value,
    );
    if (!matches) continue;

    if (action === "show") showMatched = true;
    if (action === "hide") hideMatched = true;
    if (action === "enable") enableMatched = true;
    if (action === "disable") disableMatched = true;
    if (action === "start") startMatched = true;
    if (action === "stop") stopMatched = true;
  }

  return {
    visible: visibilityReady
      ? hideMatched ? false : hasShowRule ? showMatched : baseline.visible
      : baseline.visible,
    enabled: enabledReady
      ? disableMatched ? false : hasEnableRule ? enableMatched : baseline.enabled
      : baseline.enabled,
    hasVisibilityRules: hasShowRule || hasHideRule,
    hasEnabledRules: hasEnableRule || hasDisableRule,
    hasControlRules: hasStartRule || hasStopRule,
    visibilityReady,
    enabledReady,
    controlReady,
    controlAction: controlReady
      ? stopMatched ? "stop" : startMatched ? "start" : null
      : null,
  };
}

export function hasConditionalRules(blocks: unknown): boolean {
  if (!Array.isArray(blocks)) return false;
  return blocks.some((block) => {
    if (!block || typeof block !== "object") return false;
    const value = block as { conditions?: unknown; children?: unknown };
    return (Array.isArray(value.conditions) && value.conditions.length > 0)
      || hasConditionalRules(value.children);
  });
}
